<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import {
  ArrowLeft,
  Check,
  Download,
  Eye,
  Inbox,
  Save,
  Send,
} from "lucide-vue-next";
import type { ChecklistStatus, ProjectTask, V2Asset } from "@/types";
import { api, apiBlob } from "@/services/api";
import { v2 } from "@/services/v2";
import { v3 } from "@/services/v3";
import { useProjectStore } from "@/stores/projects";
import { useSessionStore } from "@/stores/session";
import ProjectProgressBar from "@/modules/progress/ProjectProgressBar.vue";
import ProjectTimelineView from "@/modules/progress/ProjectTimelineView.vue";
import AssetPreviewModal from "@/components/AssetPreviewModal.vue";

const store = useProjectStore();
const session = useSessionStore();
const statusFilter = ref("all");
const clientFilter = ref("all");
const selectedTaskId = ref<number | null>(null);
const plannedStartAt = ref("");
const plannedEndAt = ref("");
const todoStatus = ref<Record<number, ChecklistStatus>>({});
const blockReasons = ref<Record<number, string>>({});
const todoStarts = ref<Record<number, string>>({});
const todoEnds = ref<Record<number, string>>({});
const deliveryName = ref("");
const deliverySummary = ref("");
const deliveryUrl = ref("");
const previewAsset = ref<V2Asset | null>(null);
const impactForms = ref<
  Record<
    number,
    {
      summaryZh: string;
      clientSummary: string;
      scheduleImpact: string;
      taskTitle: string;
      todoTitles: string;
    }
  >
>({});
const loading = ref(false);
let refreshTimer: number | undefined;

const queue = computed(() => store.teamQueue?.projects || []);
const counts = computed(() => store.teamQueue?.counts || {});
const clients = computed(() =>
  Array.from(new Map(queue.value.map((project) => [project.client_id, project.client_name])).entries()),
);
const filteredQueue = computed(() =>
  queue.value.filter((project) => {
    const statusMatches = statusFilter.value === "all" || project.lifecycle_state === statusFilter.value;
    const clientMatches = clientFilter.value === "all" || project.client_id === Number(clientFilter.value);
    return statusMatches && clientMatches;
  }),
);
const selectedTask = computed<ProjectTask | null>(() =>
  store.active?.tasks.find((task) => task.id === selectedTaskId.value) || null,
);
const stageLabel = (stage: string) =>
  ({
    admin_review: "待管理员分析",
    admin_client_review: "待客户确认管理员方案",
    admin_confirmed: "客户已确认，待分派",
    admin_revision: "方案调整中",
    assigned: "已分派",
    submitted: "待接单",
    team_accepted: "已接单",
    scheduled: "已排期",
    in_progress: "执行中",
    awaiting_acceptance: "待客户验收",
    revision: "修改中",
    accepted: "已完成",
    completed: "已完成",
  })[stage] || stage;

async function loadQueue() {
  await store.loadTeamQueue();
}
async function exportProject(format: "docx" | "txt") {
  if (!store.active) return;
  const url = URL.createObjectURL(await v3.exportBrief(store.active.id, format, "team"));
  const link = document.createElement("a");
  link.href = url;
  link.download = `brief-${store.active.id}-team.${format}`;
  link.click();
  URL.revokeObjectURL(url);
}
async function selectProject(id: number) {
  loading.value = true;
  try {
    await store.loadTeamProject(id);
    selectedTaskId.value = store.active?.tasks[0]?.id || null;
    syncTask();
  } finally {
    loading.value = false;
  }
}
function syncTask() {
  const task = selectedTask.value;
  plannedStartAt.value = task?.planned_start_at || "";
  plannedEndAt.value = task?.planned_end_at || task?.expected_at || "";
  todoStatus.value = Object.fromEntries(
    (store.active?.tasks || []).flatMap((item) =>
      item.checklist.map((todo) => [todo.id, todo.status]),
    ),
  );
  blockReasons.value = Object.fromEntries(
    (store.active?.tasks || []).flatMap((item) =>
      item.checklist.map((todo) => [todo.id, todo.blocked_reason || ""]),
    ),
  );
  todoStarts.value = Object.fromEntries(
    (store.active?.tasks || []).flatMap((item) =>
      item.checklist.map((todo) => [todo.id, todo.planned_start_at || ""]),
    ),
  );
  todoEnds.value = Object.fromEntries(
    (store.active?.tasks || []).flatMap((item) =>
      item.checklist.map((todo) => [todo.id, todo.planned_end_at || ""]),
    ),
  );
}
const checklistGroups = (task: ProjectTask) =>
  task.checklist.filter((item) => !item.parent_item_id);
const checklistChildren = (task: ProjectTask, parentId: number) =>
  task.checklist.filter((item) => Number(item.parent_item_id) === Number(parentId));
const statusLabel = (status: string) =>
  ({ pending: "待处理", in_progress: "进行中", blocked: "阻塞", done: "已完成", cancelled: "已取消" })[status] || status;
function closeProject() {
  store.active = null;
  selectedTaskId.value = null;
}
async function run(action: () => Promise<unknown>) {
  loading.value = true;
  try {
    await action();
    syncTask();
  } finally {
    loading.value = false;
  }
}
async function scheduleTask() {
  if (!selectedTask.value) return;
  await run(() =>
    store.scheduleTask(selectedTask.value!.id, {
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
      rowVersion: selectedTask.value!.row_version,
    }),
  );
}
async function saveTodo(itemId: number, rowVersion: number) {
  const status = todoStatus.value[itemId];
  await run(() =>
    store.updateChecklistStatus(itemId, {
      status,
      reason: status === "blocked" ? blockReasons.value[itemId] : "",
      plannedStartAt: todoStarts.value[itemId] || null,
      plannedEndAt: todoEnds.value[itemId] || null,
      rowVersion,
    }),
  );
}
async function deliver() {
  if (!selectedTask.value || !deliveryName.value.trim()) return;
  await run(async () => {
    await api(`/tasks/${selectedTask.value!.id}/deliveries`, {
      method: "POST",
      body: JSON.stringify({
        versionName: deliveryName.value,
        summary: deliverySummary.value,
        resultUrl: deliveryUrl.value,
      }),
    });
    deliveryName.value = "";
    deliverySummary.value = "";
    deliveryUrl.value = "";
    await store.loadTeamProject(store.active!.id);
    await loadQueue();
  });
}
async function uploadAsset(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !selectedTask.value) return;
  await run(async () => {
    await v2.uploadTaskAsset(selectedTask.value!.id, file);
    await store.loadTeamProject(store.active!.id);
  });
  input.value = "";
}
function impactForm(changeId: number) {
  impactForms.value[changeId] ||= {
    summaryZh: "",
    clientSummary: "",
    scheduleImpact: "",
    taskTitle: "",
    todoTitles: "",
  };
  return impactForms.value[changeId];
}
async function prepareImpact(changeId: number) {
  const form = impactForm(changeId);
  await run(async () => {
    await v2.prepareChangeImpact(changeId, {
      summaryZh: form.summaryZh,
      clientSummary: form.clientSummary || form.summaryZh,
      scheduleImpact: form.scheduleImpact,
      proposedTasks: form.taskTitle.trim()
        ? [
            {
              title: form.taskTitle,
              scope: form.summaryZh,
              acceptanceCriteria: ["完成已批准的变更范围"],
              checklist: form.todoTitles
                .split("\n")
                .map((title) => title.trim())
                .filter(Boolean)
                .map((title) => ({
                  title,
                  acceptanceCriteria: ["该变更执行项已完成"],
                })),
            },
          ]
        : [],
    });
    await store.loadTeamProject(store.active!.id);
    await loadQueue();
  });
}
async function download(asset: V2Asset) {
  const url = URL.createObjectURL(await apiBlob(asset.downloadUrl.slice(4)));
  const link = document.createElement("a");
  link.href = url;
  link.download = asset.originalName;
  link.click();
  URL.revokeObjectURL(url);
}
onMounted(() => {
  store.active = null;
  selectedTaskId.value = null;
  void loadQueue();
  refreshTimer = window.setInterval(() => void loadQueue(), 10000);
});
onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
</script>

<template>
  <div class="team">
    <header class="page-head" :class="{ 'mobile-hidden': store.active }">
      <div><h1>团队工作台</h1><p>按完整方案和 To-do 执行，实时同步客户可见进度。</p></div>
    </header>
    <section class="metrics" :class="{ 'mobile-hidden': store.active }">
      <span><b>{{ counts.total || 0 }}</b>全部项目</span>
      <span><b>{{ counts.submitted || 0 }}</b>待接单</span>
      <span><b>{{ counts.inProgress || 0 }}</b>执行中</span>
      <span class="warn"><b>{{ counts.blocked || 0 }}</b>阻塞</span>
      <span><b>{{ counts.awaitingAcceptance || 0 }}</b>待客户验收</span>
      <span class="warn"><b>{{ counts.delayed || 0 }}</b>逾期</span>
    </section>
    <div class="work-layout">
      <aside class="queue-panel" :class="{ 'mobile-hidden': store.active }">
        <div class="filters">
          <select v-model="statusFilter" class="select"><option value="all">全部状态</option><option v-if="session.user?.role === 'admin'" value="admin_review">待管理员分析</option><option value="assigned">已分派</option><option value="scheduled">已排期</option><option value="in_progress">执行中</option><option value="awaiting_acceptance">待验收</option></select>
          <select v-model="clientFilter" class="select"><option value="all">全部客户</option><option v-for="client in clients" :key="client[0]" :value="client[0]">{{ client[1] }}</option></select>
        </div>
        <button v-for="project in filteredQueue" :key="project.id" class="queue-row" :class="{ active: store.active?.id === project.id }" @click="selectProject(project.id)">
          <span><b>{{ project.title }}</b><small>{{ project.client_name }} · {{ stageLabel(project.lifecycle_state) }}</small></span>
          <ProjectProgressBar :progress="project.progress" compact />
        </button>
        <p v-if="!filteredQueue.length" class="empty"><Inbox :size="24" />当前筛选下没有项目。</p>
      </aside>
      <main v-if="store.active" class="project-detail">
        <button class="mobile-back" @click="closeProject"><ArrowLeft :size="18" />返回项目列表</button>
        <header class="project-head">
          <div><span>{{ store.active.client_name }}</span><h2>{{ store.active.title }}</h2><p>{{ stageLabel(store.active.lifecycle_state) }}</p></div>
          <div class="head-actions"><button class="btn" @click="exportProject('docx')"><Download :size="16"/>Word</button><button class="btn" @click="exportProject('txt')"><Download :size="16"/>TXT</button><button v-if="session.user?.role === 'admin' && store.active.lifecycle_state === 'submitted'" class="btn btn--primary" :disabled="loading" @click="run(store.acceptProject)"><Check :size="17" />接收旧项目</button></div>
        </header>
        <ProjectProgressBar :progress="store.active.progress" />
        <section class="full-plan"><h3>完整执行方案</h3><p>{{ store.active.solution?.teamContent.fullPlan || store.active.solution?.teamContent.summary || "暂无完整方案" }}</p><div v-if="store.active.solution?.teamContent.researchSources?.length" class="team-sources"><b>参考资料</b><a v-for="source in store.active.solution.teamContent.researchSources" :key="source.url" :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.title }}</a></div></section>
        <section class="requirement-section">
          <header><h3>客户已确认要求</h3><span>{{ store.active.reviewRequirements.length }} 项</span></header>
          <article v-for="requirement in store.active.reviewRequirements" :key="requirement.id">
            <span>{{ requirement.category }}</span><div><b>{{ requirement.title }}</b><p>{{ requirement.description }}</p></div>
          </article>
          <p v-if="!store.active.reviewRequirements.length" class="muted">暂无客户核查事项。</p>
        </section>
        <section v-if="store.active.changeRequests.length" class="change-section">
          <h3>客户变更</h3>
          <article v-for="change in store.active.changeRequests" :key="String(change.id)">
            <header><b>变更 #{{ change.id }}</b><span>{{ change.team_status }}</span></header>
            <p>{{ (change.impact as any)?.summary_zh || (change.impact as any)?.source || "客户提交了补充要求" }}</p>
            <div v-if="change.team_status === 'new' || change.client_status === 'confirmed_by_client'" class="impact-form">
              <textarea v-model="impactForm(Number(change.id)).summaryZh" class="textarea" placeholder="内部中文影响说明"></textarea>
              <textarea v-model="impactForm(Number(change.id)).clientSummary" class="textarea" placeholder="给客户看的影响说明"></textarea>
              <input v-model="impactForm(Number(change.id)).scheduleImpact" class="input" placeholder="排期影响" />
              <input v-model="impactForm(Number(change.id)).taskTitle" class="input" placeholder="新增任务名称（没有新增范围可留空）" />
              <textarea v-model="impactForm(Number(change.id)).todoTitles" class="textarea" placeholder="新增 To-do，每行一项"></textarea>
              <button class="btn btn--primary" @click="prepareImpact(Number(change.id))">提交客户确认</button>
            </div>
            <p v-else class="muted">客户状态：{{ change.client_status }}</p>
          </article>
        </section>
        <section class="task-area">
          <nav class="task-tabs"><button v-for="task in store.active.tasks" :key="task.id" :class="{ active: selectedTaskId === task.id }" @click="selectedTaskId = task.id; syncTask()">{{ task.title }}</button></nav>
          <template v-if="selectedTask">
            <div class="task-summary"><h3>{{ selectedTask.title }}</h3><p>{{ selectedTask.scope }}</p><div class="detail-list"><b>交付内容</b><span v-for="item in selectedTask.deliverables" :key="item">{{ item }}</span></div><div class="detail-list"><b>验收标准</b><span v-for="item in selectedTask.acceptance_criteria" :key="item">{{ item }}</span></div></div>
            <div class="schedule"><label>计划开始<input v-model="plannedStartAt" class="input" type="date" /></label><label>计划完成<input v-model="plannedEndAt" class="input" type="date" /></label><button class="btn" :disabled="loading" @click="scheduleTask"><Save :size="16" />保存排期</button></div>
            <section class="todo-section">
              <header><h3>两级执行清单</h3><span>进度只统计二级步骤</span></header>
              <article v-for="group in checklistGroups(selectedTask)" :key="group.id" class="todo-group">
                <header><div><span>一级阶段</span><b>{{ group.title }}</b><p>{{ group.description }}</p></div><aside><time>{{ group.planned_start_at }} 至 {{ group.planned_end_at }}</time><strong :class="group.status">{{ statusLabel(group.status) }}</strong></aside></header>
                <div v-for="item in checklistChildren(selectedTask, group.id)" :key="item.id" class="todo-row">
                  <div class="todo-copy"><span>二级步骤</span><b>{{ item.title }}</b><p>{{ item.description }}</p><small>核查：{{ item.acceptance_criteria.join("；") || "按任务验收标准" }}</small></div>
                  <div class="todo-dates"><label>开始<input v-model="todoStarts[item.id]" class="input" type="date" /></label><label>完成<input v-model="todoEnds[item.id]" class="input" type="date" /></label></div>
                  <select v-model="todoStatus[item.id]" class="select"><option value="pending">待处理</option><option value="in_progress">进行中</option><option value="blocked">阻塞</option><option value="done">已完成</option><option value="cancelled">已取消</option></select>
                  <input v-if="todoStatus[item.id] === 'blocked'" v-model="blockReasons[item.id]" class="input block-reason" placeholder="填写阻塞原因" />
                  <button class="icon-btn" title="保存步骤日期和状态" @click="saveTodo(item.id, item.row_version)"><Save :size="16" /></button>
                </div>
              </article>
            </section>
            <section class="asset-section"><header><h3>客户执行素材</h3><label class="btn upload">补充素材<input type="file" @change="uploadAsset" /></label></header><div class="asset-list"><article v-for="asset in selectedTask.assets" :key="asset.id"><span><b>{{ asset.originalName }}</b><small>{{ asset.uploaderName }} · {{ asset.createdAt.slice(0, 10) }}</small></span><button class="icon-btn" title="弹窗预览" @click="previewAsset = asset"><Eye :size="16" /></button><button class="icon-btn" title="下载原文件" @click="download(asset)"><Download :size="16" /></button></article><p v-if="!selectedTask.assets.length" class="muted">客户尚未上传正式执行素材。</p></div></section>
            <section class="delivery"><h3>{{ session.user?.role === 'admin' ? '提交客户验收' : '提交管理员审核' }}</h3><div class="delivery-fields"><input v-model="deliveryName" class="input" placeholder="版本名称" /><input v-model="deliveryUrl" class="input" placeholder="交付链接（可选）" /><textarea v-model="deliverySummary" class="textarea" placeholder="已完成范围、核查结果和需关注的内容"></textarea><button class="btn btn--primary" :disabled="loading || !deliveryName.trim()" @click="deliver"><Send :size="16" />{{ session.user?.role === 'admin' ? '提交客户验收' : '提交管理员审核' }}</button></div></section>
          </template>
        </section>
        <ProjectTimelineView :timeline="store.timeline" />
      </main>
      <main v-else class="project-empty"><Inbox :size="32" /><p>从左侧选择项目查看完整方案和执行清单。</p></main>
    </div>
    <AssetPreviewModal :asset="previewAsset" @close="previewAsset = null" />
  </div>
</template>

<style scoped>
.team{min-height:calc(100vh - 64px);background:#f5f7f7}.page-head{padding:24px 30px 17px;background:#fff;border-bottom:1px solid var(--line)}.page-head h1{margin:0;font-size:24px}.page-head p{margin:4px 0 0;color:var(--muted)}.metrics{display:grid;grid-template-columns:repeat(6,minmax(100px,1fr));background:#fff;border-bottom:1px solid var(--line)}.metrics span{display:grid;gap:2px;padding:14px 18px;border-right:1px solid var(--line);color:var(--muted);font-size:12px}.metrics b{color:var(--ink);font-size:22px}.metrics .warn b{color:var(--danger)}.work-layout{display:grid;grid-template-columns:370px minmax(0,1fr);min-height:calc(100vh - 190px)}.queue-panel{padding:16px;border-right:1px solid var(--line);background:#fafbfb}.filters{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-bottom:10px}.queue-row{display:grid;gap:10px;width:100%;padding:13px 11px;border:0;border-bottom:1px solid var(--line);background:transparent;text-align:left}.queue-row:hover,.queue-row.active{background:#fff}.queue-row.active{box-shadow:inset 3px 0 var(--brand)}.queue-row b,.queue-row small{display:block}.queue-row small{margin-top:3px;color:var(--muted)}.empty,.project-empty{display:grid;place-items:center;gap:7px;padding:40px;color:var(--muted);text-align:center}.project-detail{display:grid;align-content:start;gap:20px;min-width:0;padding:24px 28px;background:#fff}.project-head{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;padding-bottom:17px;border-bottom:1px solid var(--line)}.project-head h2{margin:2px 0;font-size:22px}.project-head span,.project-head p{margin:0;color:var(--muted)}.project-head .btn,.schedule .btn,.delivery .btn{display:inline-flex;align-items:center;justify-content:center;gap:7px}.full-plan{padding:16px 0;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}.full-plan h3,.task-area h3,.asset-section h3,.delivery h3,.requirement-section h3{margin:0}.full-plan p{white-space:pre-wrap;line-height:1.7}.requirement-section{padding:2px 0 14px;border-bottom:1px solid var(--line)}.requirement-section>header{display:flex;justify-content:space-between}.requirement-section>header span{color:var(--muted);font-size:12px}.requirement-section article{display:grid;grid-template-columns:72px minmax(0,1fr);gap:10px;padding:9px 0;border-bottom:1px solid var(--line)}.requirement-section article>span{height:max-content;padding:3px 5px;background:#eef2f2;color:var(--muted);font-size:11px;text-align:center}.requirement-section p{margin:3px 0;color:var(--muted)}.task-tabs{display:flex;gap:6px;overflow:auto;border-bottom:1px solid var(--line)}.task-tabs button{min-height:40px;border:0;border-bottom:2px solid transparent;padding:8px 11px;background:transparent;white-space:nowrap}.task-tabs button.active{border-bottom-color:var(--brand);color:var(--brand-dark);font-weight:700}.task-summary{padding:16px 0}.task-summary>p{color:var(--muted)}.detail-list{margin-top:8px}.detail-list>b{display:block;margin-bottom:3px;font-size:12px}.detail-list span{display:inline-block;margin:2px 5px 2px 0;padding:3px 6px;background:#eef2f2;color:var(--muted);font-size:12px}.schedule{display:grid;grid-template-columns:180px 180px auto;align-items:end;gap:8px;padding:13px 0;border-top:1px solid var(--line)}label{display:grid;gap:4px;color:var(--muted);font-size:12px}.todo-section,.asset-section,.delivery{padding:16px 0;border-top:1px solid var(--line)}.todo-section>header{display:flex;align-items:center;justify-content:space-between}.todo-section>header span{color:var(--muted);font-size:12px}.todo-group{margin-top:11px;border-left:3px solid #7d9096;padding-left:12px}.todo-group>header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:10px;background:#f6f8f8}.todo-group header div>span,.todo-copy>span{display:block;color:var(--brand-dark);font-size:11px;font-weight:700}.todo-group header p,.todo-copy p{margin:4px 0;color:var(--muted)}.todo-group aside{display:grid;justify-items:end;gap:5px}.todo-group time{white-space:nowrap;color:var(--muted);font-size:11px}.todo-group strong{padding:3px 6px;background:#edf1f2;color:var(--muted);font-size:11px}.todo-group strong.in_progress{background:#e6f3fb;color:#166286}.todo-group strong.blocked{background:#fff0ed;color:var(--danger)}.todo-group strong.done{background:#e8f5ef;color:var(--success)}.todo-row{display:grid;grid-template-columns:minmax(220px,1fr) 250px 120px 36px;align-items:end;gap:8px;padding:11px 0 11px 16px;border-bottom:1px solid var(--line)}.todo-copy b,.todo-copy small{display:block}.todo-copy small{margin-top:5px;color:var(--brand-dark)}.todo-dates{display:grid;grid-template-columns:1fr 1fr;gap:7px}.block-reason{grid-column:2/4}.icon-btn{display:grid;width:36px;height:36px;place-items:center;border:1px solid var(--line);background:#fff}.asset-section>header{display:flex;align-items:center;justify-content:space-between}.upload input{display:none}.asset-list{display:grid;margin-top:8px}.asset-list article{display:grid;grid-template-columns:minmax(0,1fr) 36px 36px;align-items:center;gap:7px;padding:9px 0;border-bottom:1px solid var(--line)}.asset-list b,.asset-list small{display:block}.asset-list small{color:var(--muted)}.delivery-fields{display:grid;grid-template-columns:1fr 1fr auto;align-items:end;gap:8px;margin-top:10px}.delivery-fields .textarea{grid-column:1/3}.project-detail>:deep(.timeline){padding-top:18px;border-top:1px solid var(--line)}@media(max-width:1150px){.todo-row{grid-template-columns:minmax(200px,1fr) 220px 110px 36px}}@media(max-width:1050px){.metrics{grid-template-columns:repeat(3,1fr)}.work-layout{grid-template-columns:310px minmax(0,1fr)}.todo-row{grid-template-columns:minmax(0,1fr) 110px 36px}.todo-dates{grid-column:1/-1}.block-reason{grid-column:1/-1}.delivery-fields{grid-template-columns:1fr}.delivery-fields .textarea{grid-column:auto}}@media(max-width:750px){.page-head{padding:18px 14px}.metrics{grid-template-columns:repeat(2,1fr)}.work-layout{grid-template-columns:1fr}.queue-panel{border-right:0;border-bottom:1px solid var(--line)}.project-detail{padding:18px 14px}.schedule{grid-template-columns:1fr 1fr}.schedule .btn{grid-column:1/-1}.todo-group>header{display:grid}.todo-group aside{justify-items:start}.todo-row{grid-template-columns:minmax(0,1fr) 112px 36px;padding-left:8px}.todo-copy{grid-column:1/-1}.todo-dates{grid-column:1/-1}.delivery-fields{grid-template-columns:1fr}}
.change-section{padding:16px 0;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}.change-section h3{margin:0}.change-section>article{padding:12px 0;border-bottom:1px solid var(--line)}.change-section article>header{display:flex;justify-content:space-between}.change-section article>header span{color:var(--muted);font-size:12px}.impact-form{display:grid;grid-template-columns:1fr 1fr;gap:8px}.impact-form .btn{justify-self:start}@media(max-width:750px){.impact-form{grid-template-columns:1fr}}
.mobile-back{display:none}@media(max-width:750px){.metrics{display:flex;overflow-x:auto}.metrics span{flex:0 0 112px}.mobile-back{display:flex;align-items:center;gap:7px;width:max-content;min-height:40px;border:0;padding:0;background:transparent;color:var(--brand-dark);font-weight:700}.queue-panel.mobile-hidden{display:none}.project-empty{display:none}.project-detail{min-height:calc(100dvh - 56px - 62px - env(safe-area-inset-bottom));padding-bottom:28px}.project-head h2{font-size:19px}.filters{position:sticky;top:0;z-index:2;padding-bottom:8px;background:#fafbfb}}
.metrics{scrollbar-width:none}.metrics::-webkit-scrollbar{display:none}@media(max-width:750px){.team>.page-head.mobile-hidden,.team>.metrics.mobile-hidden{display:none}.project-detail{min-height:calc(100dvh - 56px - 62px - env(safe-area-inset-bottom))}}
.team-sources{display:flex;flex-wrap:wrap;align-items:center;gap:6px;padding-top:10px;border-top:1px solid var(--line)}.team-sources>b{width:100%;font-size:12px}.team-sources a{padding:4px 6px;border:1px solid var(--line);color:var(--brand-dark);font-size:11px;text-decoration:none}.head-actions{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:7px}@media(max-width:750px){.project-head{display:grid}.head-actions{justify-content:flex-start}.head-actions .btn{flex:1;min-width:92px}}
</style>
